import { getTimezoneOffset } from 'date-fns-tz';
import type { ChartDataRange, ChartDataType } from '@/features/charts/types/chartData';
import type { ChartType } from '@/features/charts/types/highcharts';
import { http } from '@/services/http';
import { queryApiById } from '@/services/http/queryApi';
import { downloadJsonFileFromDatalake } from '@/shared/lib/download/downloadJsonFileFromDatalake';
import type { TemplateChartSeries } from '@/shared/types/charts';

/**
 * Daily assessments carry a business date, not a wall-clock instant, so the
 * backend stamps every point at a UTC time we choose. Anchoring that stamp to
 * noon in the viewer's timezone leaves ~12h of slack either side, so no
 * rendering timezone can roll a point onto the neighbouring calendar day.
 *
 * The stamp is interpreted as UTC, so it has to *cancel* the viewer's offset:
 * Asia/Singapore (UTC+8) needs 04:00Z, which renders as 12:00 local.
 * Offsets beyond ±12 clamp into range and still land on the correct date.
 */
const getOverrideTime = (timezone: string): string => {
  const offsetHours = getTimezoneOffset(timezone) / (1000 * 60 * 60);
  const hours = Math.min(23, Math.max(0, 12 - Math.round(offsetHours)));
  return `${String(hours).padStart(2, '0')}:00`;
};

export type CustomChartDataParams = {
  fileName?: string;
  groupName?: string;
  queryFileId?: string;
  from?: string;
  to?: string;
};

export const chartsApi = {
  getChartData: async (
    templateChartSeries: TemplateChartSeries[],
    chartType: ChartType,
    timezone: string
  ): Promise<ChartDataType> => {
    const overrideTime = getOverrideTime(timezone);
    const symbols = templateChartSeries.map(({ symbol }) => {
      const clonedSymbol = structuredClone(symbol);
      clonedSymbol.overrideTime = overrideTime;
      return clonedSymbol;
    });
    const { data } = await http.data.post(
      `/go/index/graph/${chartType === 'forwardSimple' ? 'curve' : chartType}`,
      {
        symbols
      }
    );
    return data;
  },

  getSeasonalChartData: async (
    templateChartSeries: TemplateChartSeries,
    chartDataRange: ChartDataRange
  ): Promise<ChartDataType> => {
    const symbol = structuredClone(templateChartSeries.symbol);
    symbol.from = chartDataRange.from;
    symbol.to = chartDataRange.to;
    const { data } = await http.data.post(`/go/index/graph/spot/seasonal`, symbol);
    return data;
  },

  getCustomChartConfig: async (fileName?: string, groupName?: string) => {
    if (!fileName || !groupName) return null;
    return downloadJsonFileFromDatalake(fileName, groupName);
  },

  getCustomChartData: async ({
    fileName,
    groupName,
    queryFileId,
    from,
    to
  }: CustomChartDataParams) => {
    if (queryFileId) {
      return queryApiById(queryFileId, from, to);
    }
    if (fileName && groupName) {
      return downloadJsonFileFromDatalake(fileName, groupName);
    }
    return null;
  }
};
